import {
  IReport,
  IReportIntrospection,
  ReportFilterEntry,
} from '@common/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type TReportStore = {
  introspection?: Partial<IReportIntrospection>;
  setIntrospection: (report: Partial<IReportIntrospection>) => void;
  report?: Partial<IReport>;
  setReport: (report: Partial<IReport>) => void;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  isFormInitialized: boolean;
  setIsFormInitialized: (initial: boolean) => void;
  hideSaveOptions: boolean;
  setHideSaveOptions: (initial: boolean) => void;
  isOwner: boolean;
  setIsOwner: (initial: boolean) => void;
  filters?: ReportFilterEntry[];
  setFilters: (newFilters: ReportFilterEntry[]) => void;
  clearAll: () => void;
};

/**
 * Used to track the report state from the create/edit individual report flow
 */
export const useReportStore = create<TReportStore>()(
  persist(
    set =>
      ({
        introspection: undefined,
        setIntrospection: newIntrospection =>
          set(old => ({
            introspection: {
              ...old.introspection,
              ...newIntrospection,
            },
          })),
        report: {
          interval: 'all_time',
          options: {
            patient_report_type: 'all_patients',
            include_future_tasks: false,
            select: [],
          },
        },
        setReport: (newReport: Partial<IReport>) =>
          set(old => ({
            report: {
              ...(old.report ?? {}),
              ...newReport,
              options: {
                ...old.report?.options,
                ...newReport.options,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any,
            },
          })),
        isModalOpen: false,
        setIsModalOpen: open => set(() => ({ isModalOpen: open })),
        isFormInitialized: false,
        setIsFormInitialized: initial =>
          set(() => ({ isFormInitialized: initial })),
        hideSaveOptions: false,
        setHideSaveOptions: hide => set(() => ({ hideSaveOptions: hide })),
        isOwner: false,
        setIsOwner: owner => set(() => ({ isOwner: owner })),
        filters: undefined,
        setFilters: newFilters => set(() => ({ filters: newFilters })),
        clearAll: () =>
          set(() => ({
            filters: undefined,
            report: {
              interval: 'all_time',
              options: {
                patient_report_type: 'all_patients',
                include_future_tasks: false,
                select: [],
              },
            },
            isModalOpen: false,
            isFormInitialized: false,
            hideSaveOptions: false,
          })),
      }) as TReportStore,
    {
      name: 'report-store',
      partialize: state => ({
        introspection: state.introspection,
        report: state.report,
        filters: state.filters,
      }),
    },
  ),
);
